import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked.
 *
 * The debounced function comes with a cancel method to cancel delayed func invocations
 * and a flush method to immediately invoke them.
 *
 * @template T - The type of the function to debounce
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @param options - The options object
 * @param options.leading - Specify invoking on the leading edge of the timeout
 * @returns Returns the new debounced function with cancel and flush methods
 *
 * @example
 * // Basic usage with trailing execution (default)
 * const debouncedSearch = debounce((searchTerm: string) => {
 *   console.log('Searching for:', searchTerm)
 * }, 300)
 *
 * @example
 * // With leading edge execution
 * const debouncedSave = debounce((data: FormData) => {
 *   saveData(data)
 * }, 1000, { leading: true })
 *
 * @example
 * // Cancel pending execution
 * const debouncedFn = debounce(() => console.log('executed'), 1000)
 * debouncedFn()
 * debouncedFn.cancel() // Cancels the pending execution
 *
 * @example
 * // Immediately flush pending execution
 * const debouncedFn = debounce(() => console.log('executed'), 1000)
 * debouncedFn()
 * debouncedFn.flush() // Immediately executes and cancels pending
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  options: { leading?: boolean } = {}
): T & { cancel: () => void; flush: () => ReturnType<T> | undefined } {
  let timeoutId: NodeJS.Timeout | undefined
  let lastArgs: Parameters<T> | undefined
  let lastThis: any
  let result: ReturnType<T> | undefined
  let lastCallTime: number | undefined
  let lastInvokeTime = 0

  const { leading = false } = options

  function invokeFunc(time: number): ReturnType<T> {
    const args = lastArgs!
    const thisArg = lastThis

    lastArgs = undefined
    lastThis = undefined
    lastInvokeTime = time
    result = func.apply(thisArg, args)
    return result
  }

  function leadingEdge(time: number): ReturnType<T> {
    // Reset any `maxWait` timer.
    lastInvokeTime = time
    // Start the timer for the trailing edge.
    timeoutId = setTimeout(timerExpired, wait)
    // Invoke the leading edge.
    return leading ? invokeFunc(time) : result!
  }

  function remainingWait(time: number): number {
    const timeSinceLastCall = time - lastCallTime!
    const timeSinceLastInvoke = time - lastInvokeTime
    const timeWaiting = wait - timeSinceLastCall

    return Math.min(timeWaiting, wait - timeSinceLastInvoke)
  }

  function shouldInvoke(time: number): boolean {
    const timeSinceLastCall = time - lastCallTime!
    const timeSinceLastInvoke = time - lastInvokeTime

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (
      lastCallTime === undefined ||
      timeSinceLastCall >= wait ||
      timeSinceLastCall < 0 ||
      timeSinceLastInvoke >= wait
    )
  }

  function timerExpired(): ReturnType<T> | undefined {
    const time = Date.now()
    if (shouldInvoke(time)) {
      return trailingEdge(time)
    }
    // Restart the timer.
    timeoutId = setTimeout(timerExpired, remainingWait(time))
    return undefined
  }

  function trailingEdge(time: number): ReturnType<T> {
    timeoutId = undefined

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (lastArgs) {
      return invokeFunc(time)
    }
    lastArgs = undefined
    lastThis = undefined
    return result!
  }

  function cancel(): void {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
    }
    lastInvokeTime = 0
    lastArgs = undefined
    lastCallTime = undefined
    lastThis = undefined
    timeoutId = undefined
  }

  function flush(): ReturnType<T> | undefined {
    return timeoutId === undefined ? result : trailingEdge(Date.now())
  }

  function debounced(
    this: any,
    ...args: Parameters<T>
  ): ReturnType<T> | undefined {
    const time = Date.now()
    const isInvoking = shouldInvoke(time)

    lastArgs = args
    lastThis = this
    lastCallTime = time

    if (isInvoking) {
      if (timeoutId === undefined) {
        return leadingEdge(lastCallTime)
      }
    }
    if (timeoutId === undefined) {
      timeoutId = setTimeout(timerExpired, wait)
    }
    return result
  }

  debounced.cancel = cancel
  debounced.flush = flush

  return debounced as T & {
    cancel: () => void
    flush: () => ReturnType<T> | undefined
  }
}

// =============================================================================
// TIME UTILITY FUNCTIONS
// =============================================================================
// Extracted from TimeDropdown.tsx to prevent circular dependencies
// These utilities handle time conversions between seconds and time strings

/**
 * Converts seconds to a formatted time string
 * @param seconds - Number of seconds (0-86399 for a full day)
 * @param format - '12h' (with AM/PM) or '24h' (military time)
 * @returns Formatted time string (e.g., "2:30 PM" or "14:30")
 */
export function secondsToTimeString(
  seconds: number,
  format: '12h' | '24h' = '12h'
): string {
  try {
    if (typeof seconds !== 'number' || !isFinite(seconds)) {
      return '--:--'
    }

    // Clamp to valid range (0-86399 seconds = 00:00:00 to 23:59:59)
    const validSeconds = Math.max(0, Math.min(86399, Math.floor(seconds)))
    const hours = Math.floor(validSeconds / 3600)
    const minutes = Math.floor((validSeconds % 3600) / 60)

    if (format === '12h') {
      const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
      const ampm = hours < 12 ? 'AM' : 'PM'
      return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`
    } else {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
    }
  } catch (error) {
    return '--:--'
  }
}

/**
 * Converts a time string to seconds
 * @param timeStr - Time string in various formats:
 *   - "2:30 PM" or "2:30PM" (12-hour with AM/PM)
 *   - "14:30" (24-hour)
 *   - "2:30" (assumes 24-hour if no AM/PM)
 * @returns Number of seconds from midnight (0-86399)
 */
export function timeStringToSeconds(timeStr: string): number {
  try {
    if (!timeStr || typeof timeStr !== 'string') {
      throw new Error('Invalid time string: must be a non-empty string')
    }

    const trimmed = timeStr.trim().toUpperCase()
    if (!trimmed) {
      throw new Error('Invalid time string: empty after trimming')
    }

    // Check for AM/PM indicator
    const hasAmPm = /[AP]M/.test(trimmed)
    const isAm = /AM/.test(trimmed)
    const isPm = /PM/.test(trimmed)

    // Extract time part (remove AM/PM if present)
    const timePart = trimmed.replace(/\s*[AP]M\s*/, '').trim()

    // Parse hours and minutes
    const parts = timePart.split(':')
    if (parts.length !== 2) {
      throw new Error(
        `Invalid time format: "${timeStr}" - expected format HH:MM`
      )
    }

    const hoursStr = parts[0].trim()
    const minutesStr = parts[1].trim()

    const parsedHours = parseInt(hoursStr, 10)
    const minutes = parseInt(minutesStr, 10)

    // Validate minutes
    if (isNaN(minutes) || minutes < 0 || minutes > 59) {
      throw new Error(`Invalid minutes: "${minutesStr}" - must be 0-59`)
    }

    // Convert to 24-hour format if needed
    let hours = parsedHours
    if (hasAmPm) {
      if (isNaN(parsedHours) || parsedHours < 1 || parsedHours > 12) {
        throw new Error(
          `Invalid 12-hour format hours: "${hoursStr}" - must be 1-12`
        )
      }
      if (isPm && hours !== 12) {
        hours += 12
      } else if (isAm && hours === 12) {
        hours = 0
      }
    } else {
      // 24-hour format validation
      if (isNaN(parsedHours) || parsedHours < 0 || parsedHours > 23) {
        throw new Error(
          `Invalid 24-hour format hours: "${hoursStr}" - must be 0-23`
        )
      }
    }

    return hours * 3600 + minutes * 60
  } catch (error) {
    console.error('timeStringToSeconds error:', error)
    throw error
  }
}

// =============================================================================
// AI ASSISTANT UTILITY FUNCTIONS
// =============================================================================

/**
 * Generates a concise, scannable title from an AI conversation message
 * Extracts key entities and actions to create readable 40-50 character summaries
 * @param message - The first user message in a conversation
 * @returns Shortened, semantic title for conversation list display
 */
export function generateConversationTitle(message: string): string {
  // ✅ ZERO DEFECT: Explicit validation with fallback for display-only function
  // This is a UI utility, not a data integrity function, so we use safe fallback
  if (!message || typeof message !== 'string') {
    return 'Untitled Conversation'
  }

  const trimmed = message.trim()
  if (!trimmed) {
    return 'Empty Message'
  }

  // Extract key entities using pattern matching
  const vehicleMatch = trimmed.match(
    /(?:add|create|new)\s+(?:a\s+)?(?:delivery\s+)?(?:vehicle|truck)/i
  )
  const jobMatch = trimmed.match(
    /(?:add|create|new)\s+(?:a\s+)?(?:delivery\s+)?(?:job|order|task)/i
  )
  const locationMatch = trimmed.match(/(?:add|create|new)\s+(?:a\s+)?location/i)

  // Extract capacity if mentioned
  const capacityMatch = trimmed.match(/(\d+)\s*(kg|ton|liters?|l)/i)
  const capacity = capacityMatch
    ? `${capacityMatch[1]}${capacityMatch[2].toLowerCase()}`
    : null

  // Extract location/city if mentioned
  const cityMatch = trimmed.match(/in\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i)
  const city = cityMatch ? cityMatch[1] : null

  // Generate semantic title based on detected entities
  if (vehicleMatch) {
    const parts = ['Vehicle']
    if (city) parts.push(city)
    if (capacity) parts.push(`(${capacity})`)
    return parts.join(': ')
  }

  if (jobMatch) {
    const parts = ['Job']
    if (city) parts.push(city)
    return parts.join(': ')
  }

  if (locationMatch) {
    const parts = ['Location']
    if (city) parts.push(city)
    return parts.join(': ')
  }

  // Fallback: Use first 45 characters with smart truncation at word boundary
  if (trimmed.length <= 45) {
    return trimmed
  }

  const truncated = trimmed.substring(0, 45)
  const lastSpace = truncated.lastIndexOf(' ')
  return lastSpace > 20
    ? truncated.substring(0, lastSpace) + '...'
    : truncated + '...'
}

/**
 * Formats timestamp as user-friendly relative time
 * Shows "Just now", "2 min ago", "Today 4:32 PM", "Yesterday", or "Jan 15"
 * @param timestamp - Unix timestamp in milliseconds
 * @returns Human-readable relative time string
 */
export function formatRelativeTime(timestamp: number): string {
  if (typeof timestamp !== 'number' || !isFinite(timestamp)) {
    throw new Error('formatRelativeTime: timestamp must be a valid number')
  }

  const now = Date.now()
  const diffMs = now - timestamp
  const diffMin = Math.floor(diffMs / 60000)
  const diffDays = Math.floor(diffMs / 86400000)

  // Just now (< 1 minute)
  if (diffMin < 1) {
    return 'Just now'
  }

  // Minutes ago (1-59 minutes)
  if (diffMin < 60) {
    return `${diffMin} min ago`
  }

  // Hours ago (1-23 hours, same day)
  const messageDate = new Date(timestamp)
  const todayStart = new Date(now)
  todayStart.setHours(0, 0, 0, 0)

  if (timestamp >= todayStart.getTime()) {
    const timeStr = messageDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    return `Today ${timeStr}`
  }

  // Yesterday
  const yesterdayStart = new Date(todayStart)
  yesterdayStart.setDate(yesterdayStart.getDate() - 1)

  if (timestamp >= yesterdayStart.getTime()) {
    return 'Yesterday'
  }

  // This week (within 7 days)
  if (diffDays < 7) {
    return messageDate.toLocaleDateString('en-US', { weekday: 'short' })
  }

  // Older (show date)
  return messageDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}
